import React, { useState } from "react";

const ICON_OPTIONS = ["📅", "🕌", "📖", "🎤", "🌟", "📚", "👨‍👩‍👧‍👦", "🎉", "🕐", "🌙"];

/**
 * EventManager — admin component to create, edit, toggle, and delete events.
 *
 * Props:
 *   events         – array of all event objects
 *   onCreateEvent  – async (eventData) => newEvent
 *   onUpdateEvent  – async (id, updates) => updatedEvent
 *   onDeleteEvent  – async (id) => void
 */
function EventManager({ events, onCreateEvent, onUpdateEvent, onDeleteEvent }) {
  const [icon, setIcon] = useState("📅");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [day, setDay] = useState("");
  const [time, setTime] = useState("");

  // Editing state
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  const handleCreate = async (e) => {
    e.preventDefault();
    await onCreateEvent({ icon, title, description, day, time });
    setIcon("📅");
    setTitle("");
    setDescription("");
    setDay("");
    setTime("");
  };

  const startEdit = (event) => {
    setEditingId(event.id);
    setEditData({ ...event });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const saveEdit = async () => {
    await onUpdateEvent(editingId, editData);
    setEditingId(null);
    setEditData({});
  };

  return (
    <div>
      {/* ─── Create Event Form ─── */}
      <div className="card mb-4 shadow">
        <div className="card-body">
          <h5 className="card-title mb-3">Nytt arrangement</h5>
          <form onSubmit={handleCreate}>
            <div className="row mb-3">
              <div className="col-auto">
                <label className="form-label small">Ikon</label>
                <select
                  className="form-select"
                  value={icon}
                  onChange={(e) => setIcon(e.target.value)}
                >
                  {ICON_OPTIONS.map((ic) => (
                    <option key={ic} value={ic}>{ic}</option>
                  ))}
                </select>
              </div>
              <div className="col">
                <label className="form-label small">Tittel</label>
                <input
                  className="form-control"
                  placeholder="Arrangementnavn"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label small">Beskrivelse</label>
              <textarea
                className="form-control"
                placeholder="Kort beskrivelse av arrangementet"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>
            <div className="row mb-3">
              <div className="col">
                <label className="form-label small">Dag / Dato</label>
                <input
                  className="form-control"
                  placeholder="f.eks. Hver fredag, 15. mars"
                  value={day}
                  onChange={(e) => setDay(e.target.value)}
                  required
                />
              </div>
              <div className="col">
                <label className="form-label small">Tid</label>
                <input
                  className="form-control"
                  placeholder="f.eks. 13:00 – 14:00"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  required
                />
              </div>
            </div>
            <button type="submit" className="btn btn-primary w-100">
              Legg til arrangement
            </button>
          </form>
        </div>
      </div>

      {/* ─── Event List ─── */}
      <h5 className="mb-3">Alle arrangementer ({events.length})</h5>
      {events.length === 0 && (
        <p className="text-muted">Ingen arrangementer opprettet ennå.</p>
      )}

      <div className="list-group">
        {events.map((event) => (
          <div key={event.id} className="list-group-item">
            {editingId === event.id ? (
              /* ── Inline Edit Mode ── */
              <div>
                <div className="row g-2 mb-2">
                  <div className="col-auto">
                    <select
                      className="form-select form-select-sm"
                      value={editData.icon}
                      onChange={(e) => setEditData({ ...editData, icon: e.target.value })}
                    >
                      {ICON_OPTIONS.map((ic) => (
                        <option key={ic} value={ic}>{ic}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col">
                    <input
                      className="form-control form-control-sm"
                      value={editData.title}
                      onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                    />
                  </div>
                </div>
                <textarea
                  className="form-control form-control-sm mb-2"
                  value={editData.description}
                  onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                  rows={2}
                />
                <div className="row g-2 mb-2">
                  <div className="col">
                    <input
                      className="form-control form-control-sm"
                      placeholder="Dag"
                      value={editData.day}
                      onChange={(e) => setEditData({ ...editData, day: e.target.value })}
                    />
                  </div>
                  <div className="col">
                    <input
                      className="form-control form-control-sm"
                      placeholder="Tid"
                      value={editData.time}
                      onChange={(e) => setEditData({ ...editData, time: e.target.value })}
                    />
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <button className="btn btn-sm btn-success" onClick={saveEdit}>
                    Lagre
                  </button>
                  <button className="btn btn-sm btn-secondary" onClick={cancelEdit}>
                    Avbryt
                  </button>
                </div>
              </div>
            ) : (
              /* ── Display Mode ── */
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="mb-1">
                    {event.icon} {event.title}
                    <span className={`badge ms-2 ${event.visible ? "bg-success" : "bg-secondary"}`}>
                      {event.visible ? "Synlig" : "Skjult"}
                    </span>
                  </h6>
                  <small className="text-muted d-block">{event.description}</small>
                  <small className="text-muted">📅 {event.day} · 🕐 {event.time}</small>
                </div>
                <div className="d-flex gap-1 flex-shrink-0 ms-3">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => startEdit(event)}
                    title="Rediger"
                  >
                    ✏️
                  </button>
                  <button
                    className="btn btn-sm btn-outline-warning"
                    onClick={() => onUpdateEvent(event.id, { visible: !event.visible })}
                    title={event.visible ? "Skjul" : "Vis"}
                  >
                    {event.visible ? "👁️" : "🚫"}
                  </button>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => {
                      if (window.confirm(`Slett "${event.title}"?`)) {
                        onDeleteEvent(event.id);
                      }
                    }}
                    title="Slett"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default EventManager;
